import { useContext } from "react";
import { UnauthenticatedLocaleContext } from "../providers/UnauthenticatedLocaleProvider";
import { AuthenticatedLocaleContext } from "../providers/AuthenticatedLocaleProvider";

// Bundled copy for the generic error screens. They can render where Accent translations are not
// available (root ErrorBoundary, providers above TranslationsProvider, failed locale requests),
// so this copy intentionally lives in code and not in Accent
export type ErrorFallbackCopy = { title: string; description: string; notFound: string };

const DEFAULT_LOCALE = "en";

const FALLBACK_COPY: Record<string, ErrorFallbackCopy> = {
  en: {
    title: "Oops... something went wrong!",
    description: "Please try again later.",
    notFound: "Error 404",
  },
  es: {
    title: "¡Vaya! Algo ha salido mal.",
    description: "Por favor, inténtalo de nuevo más tarde.",
    notFound: "Error 404",
  },
  ca: {
    title: "Vaja! Alguna cosa ha anat malament.",
    description: "Si us plau, torna-ho a provar més tard.",
    notFound: "Error 404",
  },
  pt: {
    title: "Ops... algo correu mal!",
    description: "Por favor, tente novamente mais tarde.",
    notFound: "Erro 404",
  },
  fr: {
    title: "Oups... une erreur s'est produite !",
    description: "Veuillez réessayer plus tard.",
    notFound: "Erreur 404",
  },
  it: {
    title: "Ops... qualcosa è andato storto!",
    description: "Riprova più tardi.",
    notFound: "Errore 404",
  },
  eu: {
    title: "Ene... zerbait gaizki atera da!",
    description: "Mesedez, saiatu berriro geroago.",
    notFound: "404 errorea",
  },
};

const toLanguage = (locale?: string | null) => locale?.toLowerCase().split(/[-_]/)[0];

// Reads the contexts directly (not useLocale) because it must work above the auth provider too
export const useErrorFallbackCopy = (): ErrorFallbackCopy => {
  const authenticatedLocale = useContext(AuthenticatedLocaleContext)?.locale;
  const unauthenticatedLocale = useContext(UnauthenticatedLocaleContext)?.locale;

  const candidates = [
    toLanguage(authenticatedLocale),
    toLanguage(unauthenticatedLocale),
    toLanguage(typeof navigator === "undefined" ? undefined : navigator.language),
  ];
  const language = candidates.find((candidate) => candidate && FALLBACK_COPY[candidate]);

  return FALLBACK_COPY[language ?? DEFAULT_LOCALE];
};
